import React, {
  useCallback, useReducer, useState,
} from 'react';
import {
  Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel,
} from '@mui/material';
import PropTypes from 'prop-types';

import FormField from './components/FormField';

import texts from './texts.json';
import {
  FormContainer, GridContainer, SectionContainer, SectionHeader,
} from './styles';

const initialMealPlanData = {
  age: null,
  biologicalSex: null,
  height: null,
  weight: null,
  preferredCuisines: null,
  availableIngredients: null,
  budget: null,
  cookingSkillLevel: null,
  mealPrepTime: null,
  dietaryGoal: null,
  dietaryRestrictionsOrAllergies: null,
  targetDailyCalories: null,
  proteinPct: null,
  carbPct: null,
  fatPct: null,
  additionalNotes: null,
};

function mapToProps(params) {
  const schemaTypes = {
    age: 'number',
    biologicalSex: 'string',
    height: 'number',
    weight: 'number',
    preferredCuisines: 'string',
    availableIngredients: 'string',
    budget: 'string',
    cookingSkillLevel: 'string',
    mealPrepTime: 'string',
    dietaryGoal: 'string',
    dietaryRestrictionsOrAllergies: 'string',
    targetDailyCalories: 'number',
    proteinPct: 'number',
    carbPct: 'number',
    fatPct: 'number',
    additionalNotes: 'string',
  };

  const userOverrides = Object.entries(params).reduce((acc, [key, value]) => {
    if (value !== null && value !== '' && schemaTypes[key] && key !== 'additionalNotes') {
      acc[key] = schemaTypes[key] === 'number' ? Number(value) : String(value);
    }
    return acc;
  }, {});

  const additionalQuery = params.additionalNotes && String(params.additionalNotes).trim()
    ? String(params.additionalNotes)
    : null;

  return { userOverrides, additionalQuery };
}

function mealPlanDataReducer(state, action) {
  switch (action.type) {
    case 'SET_INITIAL_DATA':
      return action.payload;
    case 'SET_DATA':
      return { ...state, [action.payload.key]: action.payload.value };
    case 'CLEAR_DATA':
      return initialMealPlanData;
    default:
      return state;
  }
}

const genderOptions = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

const cookingSkillOptions = [
  { value: 'Beginner', label: 'Beginner' },
  { value: 'Intermediate', label: 'Intermediate' },
  { value: 'Advanced', label: 'Advanced' },
];

const personalInfoFields = [
  {
    key: 'biologicalSex',
    label: 'Biological Sex',
    type: 'select',
    options: genderOptions,
    placeholder: 'Select biological sex',
  },
  {
    key: 'age',
    label: 'Age',
    type: 'number',
    placeholder: 'Enter age',
  },
  {
    key: 'height',
    label: 'Height (cm)',
    type: 'number',
    placeholder: 'Enter height in cm',
  },
  {
    key: 'weight',
    label: 'Weight (kg)',
    type: 'number',
    placeholder: 'Enter weight in kg',
  },
];

const nutritionFields = [
  {
    key: 'targetDailyCalories',
    label: 'Target Daily Calories',
    type: 'number',
    placeholder: 'e.g., 2000',
  },
  {
    key: 'proteinPct',
    label: 'Protein Percentage',
    type: 'number',
    placeholder: 'e.g., 25',
  },
  {
    key: 'carbPct',
    label: 'Carbohydrate Percentage',
    type: 'number',
    placeholder: 'e.g., 45',
  },
  {
    key: 'fatPct',
    label: 'Fat Percentage',
    type: 'number',
    placeholder: 'e.g., 30',
  },
];

const preferencesFields = [
  {
    key: 'dietaryGoal',
    label: 'Dietary Goal',
    type: 'text',
    placeholder: 'e.g., Weight loss, Muscle gain, Maintenance',
  },
  {
    key: 'dietaryRestrictionsOrAllergies',
    label: 'Dietary Restrictions or Allergies',
    type: 'text',
    placeholder: 'e.g., Gluten-free, Nut allergy, Vegetarian',
  },
  {
    key: 'preferredCuisines',
    label: 'Preferred Cuisines',
    type: 'text',
    placeholder: 'e.g., Italian, Asian, Mediterranean',
  },
  {
    key: 'availableIngredients',
    label: 'Available Ingredients',
    type: 'text',
    placeholder: 'e.g., Chicken, Rice, Vegetables',
  },
];

const logisticsFields = [
  {
    key: 'budget',
    label: 'Weekly Budget',
    type: 'text',
    placeholder: 'e.g., $100/week',
  },
  {
    key: 'cookingSkillLevel',
    label: 'Cooking Skill Level',
    type: 'select',
    options: cookingSkillOptions,
    placeholder: 'Select cooking skill level',
  },
  {
    key: 'mealPrepTime',
    label: 'Available Meal Prep Time',
    type: 'text',
    placeholder: 'e.g., 1 hour per day',
  },
];

const additionalFields = [
  {
    key: 'additionalNotes',
    label: 'Additional Notes',
    type: 'text',
    placeholder: 'Any other relevant information',
  },
];

const AIMealPlanModal = ({
  open,
  handleClose,
  setOpen,
  isTemplate = false,
}) => {
  const [mealPlanData, dispatch] = useReducer(mealPlanDataReducer, initialMealPlanData);
  const [showForm, setShowForm] = useState(false);

  const handleFieldChange = useCallback((key) => (event) => {
    dispatch({
      type: 'SET_DATA',
      payload: {
        key,
        value: event.target.value,
      },
    });
  }, []);

  const renderFormSection = useCallback((fields, sectionTitle, gridType) => (
    <SectionContainer>
      <SectionHeader>{sectionTitle}</SectionHeader>
      <GridContainer gridType={gridType}>
        {fields.map((field) => (
          <FormField
            key={field.key}
            label={field.label}
            value={mealPlanData[field.key] ?? ''}
            onChange={handleFieldChange(field.key)}
            type={field.type}
            isSelect={field.type === 'select'}
            selectOptions={field.options}
            placeholder={field.placeholder}
            required={field.required}
          />
        ))}
      </GridContainer>
    </SectionContainer>
  ), [mealPlanData, handleFieldChange]);

  return (
    <Dialog maxWidth="xl" open={open} onClose={() => setOpen(false)}>
      <DialogTitle id="create-pod">
        {isTemplate ? texts.templateData : texts.userData}
      </DialogTitle>
      <DialogContent>
        {!isTemplate && (
          <FormControlLabel
            control={<Checkbox checked={showForm} onChange={(e) => setShowForm(e.target.checked)} color="primary" />}
            label={texts.showForm}
          />
        )}

        {(isTemplate || showForm) && (
          <FormContainer>
            {!isTemplate && renderFormSection(personalInfoFields, texts.personalInfo, 'personal')}
            {renderFormSection(nutritionFields, texts.nutritionGoals, 'nutrition')}
            {renderFormSection(preferencesFields, texts.dietaryPreferences, 'preferences')}
            {renderFormSection(logisticsFields, texts.logisticsConstraints, 'logistics')}
            {renderFormSection(additionalFields, texts.additionalConstraints, 'additional')}
          </FormContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>{texts.cancel}</Button>
        <Button
          variant="outlined"
          onClick={() => {
            const { userOverrides, additionalQuery } = mapToProps(mealPlanData);
            handleClose(userOverrides, additionalQuery);
          }}
        >
          {texts.generate}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AIMealPlanModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  setOpen: PropTypes.func.isRequired,
  isTemplate: PropTypes.bool,
};

AIMealPlanModal.defaultProps = {
  isTemplate: false,
};

export default AIMealPlanModal;
